/**
 * Le crochet du test du matériel : il branche les effets sur la machine (`deviceCheckMachine`) et
 * ne fait rien d'autre.
 *
 * Les sondes sont celles d'avant, déplacées telles quelles : l'accès au micro et à la caméra
 * (`useDeviceCheck`), la latence (`useNetworkCheck`), la liste des périphériques (`useMediaDevices`),
 * le souvenir du dernier test (`useRememberedCheck`), l'image de l'aperçu (`useCameraObstruction`),
 * le niveau du micro (`useMicrophoneLevel`). Chaque constat devient un événement ; chaque délai (le
 * temps d'écoute du micro, la réussite qu'on laisse voir) et chaque boucle (la re-sonde toutes les
 * 5 s de l'appareil en défaut, la connexion toutes les 9 s pendant son étape, le retour dans
 * l'onglet) sont posés ici, et retirés.
 *
 * Les signaux de la machine partent d'ici : les événements Amplitude du précall (avec le contexte
 * commun de l'appelant), un fil d'Ariane Bugsnag par transition, et une trace typée à chaque accès
 * refusé — un refus n'est plus absorbé.
 *
 * Rien de propre à l'élève : le public (`audience`) et le rôle de la télémétrie viennent de
 * l'appelant.
 */
import { type AudioSource } from "expo-audio"
import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react"
import { Linking } from "react-native"

import { createTelemetry, type EngineTelemetry, type TelemetryRole } from "../shared/telemetry"
import {
    CHIME_MS,
    MIC_GRACE_MS,
    RESOLVED_HOLD_MS,
    deriveDeviceCheck,
    deviceCheckReducer,
    initialDeviceCheckState,
    microphoneListening,
    type DeviceCause,
    type DeviceCheckAudience,
    type DeviceCheckSignal,
    type DeviceCheckStepId,
    type DeviceCheckView,
    type ImageReading,
    type MediaDeviceKind,
} from "./deviceCheckMachine"
import { playbackStarted } from "./playbackStarted"
import { playSoundOnce } from "./playSound"
import { trackPrecall } from "./precallTracking"
import { useCameraObstruction } from "./useCameraObstruction"
import { useDeviceCheck } from "./useDeviceCheck"
import { useMediaDevices } from "./useMediaDevices"
import { SIGNAL_THRESHOLD } from "./useMicSignal"
import { useMicrophoneLevel } from "./useMicrophoneLevel"
import { useNetworkCheck } from "./useNetworkCheck"
import { useRememberedCheck } from "./useRememberedCheck"
import { IS_WEB } from "../shared/platform"
import { useInterval } from "../shared/useInterval"
import { useSignalReducer } from "../shared/useSignalReducer"
import { pagePermissionBrowser } from "./browserPermissionPaths"
import { browserFamily } from "./verifiedMemory"

/** Le souvenir du dernier test réussi sur cet appareil (`state.precallMemory.verified`). */
export type VerifiedDeviceCheck = { signature: string; at: number; components?: Record<string, string> }

/** Le contexte commun de chaque événement Amplitude du précall : `live_session_id`, `platform`, `browser`. */
export type PrecallTrackingContext = Record<string, string | number | boolean | null>

/** Un refus d'accès : son nom suffit à le compter, le message ne dit que des mots du code. */
export class DevicePermissionDenied extends Error {
    constructor(device: "microphone" | "camera") {
        super(`accès refusé : ${device === "camera" ? "caméra" : "micro"}`)
        this.name = "DevicePermissionDenied"
    }
}

export type UseDeviceCheckMachineOptions = {
    audience: DeviceCheckAudience
    role: TelemetryRole
    isVideoMuted: boolean
    onSetVideoMuted: (muted: boolean) => void
    verified: VerifiedDeviceCheck | null
    onVerified: (verified: VerifiedDeviceCheck) => void
    /** Le périphérique choisi ici suit l'élève en séance (la salle le reprendra). */
    onDevicesChange?: (devices: { audioinput: string | null; videoinput: string | null }) => void
    tracking: PrecallTrackingContext
    /** La mélodie du test des haut-parleurs. */
    chimeSource: AudioSource
    /** Un test passe la sienne. */
    telemetry?: EngineTelemetry
    /**
     * L'écran rend les appareils avant d'entrer dans le live : l'enregistreur du micro se ferme même si
     * son étape est ouverte. Au natif, un micro ne sert bien qu'un client : la salle publiera le sien.
     * Défaut `false`.
     */
    releaseMicrophone?: boolean
}

export type DeviceCheckController = {
    view: DeviceCheckView
    /** À poser sur la vue qui contient l'aperçu : l'analyse de l'image y lit le flux. */
    previewRef: RefObject<unknown>
    /** Le niveau du micro, de 0 à 1, à l'étape du micro. */
    micLevel: number
    audioDeviceId: string | null
    videoDeviceId: string | null
    isVideoMuted: boolean
    start: () => void
    recheck: () => void
    skip: (step: DeviceCheckStepId) => void
    reopen: (step: DeviceCheckStepId) => void
    answerSpeaker: (heard: boolean) => void
    answerSeesSelf: (sees: boolean) => void
    nextAttempt: () => void
    micNoSignal: () => void
    authorize: () => void
    openSettings: () => void
    selectDevice: (kind: MediaDeviceKind, deviceId: string) => void
    playChime: () => void
    setVideoMuted: (muted: boolean) => void
    /** L'aperçu web rapporte ce qui l'empêche de s'ouvrir (`null` : une image est arrivée). */
    onPreviewError: (cause: DeviceCause | null) => void
}

type DeviceCheckProbe = {
    causes: { camera: DeviceCause; microphone: DeviceCause }
    isChecking: boolean
    run: (options?: {
        request?: boolean
        audioDeviceId?: string | null
        videoDeviceId?: string | null
        only?: "microphone" | "camera" | null
    }) => Promise<unknown>
}

export function useDeviceCheckMachine(options: UseDeviceCheckMachineOptions): DeviceCheckController {
    const { audience, role, isVideoMuted, verified, chimeSource } = options
    const optionsRef = useRef(options)
    optionsRef.current = options

    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("precall", { role }),
        // La télémétrie d'une machine montée ne change pas en route.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const onSignals = useCallback(
        (signals: DeviceCheckSignal[]) => {
            for (const signal of signals) {
                if (signal.kind === "track") {
                    trackPrecall(signal.event, { ...optionsRef.current.tracking, ...signal.properties })
                } else if (signal.kind === "transition") {
                    telemetry.transition(signal.from, signal.to, signal.event, signal.data)
                } else {
                    telemetry.error("permission", new DevicePermissionDenied(signal.device), {
                        device: signal.device,
                        cause: signal.cause,
                    })
                }
            }
        },
        [telemetry]
    )

    const [state, dispatch] = useSignalReducer(
        deviceCheckReducer,
        () =>
            initialDeviceCheckState(
                {
                    isWeb: IS_WEB,
                    browser: IS_WEB && typeof navigator !== "undefined" ? browserFamily(navigator.userAgent) : "autre",
                    permissionBrowser: pagePermissionBrowser(),
                    audience,
                },
                isVideoMuted
            ),
        onSignals
    )
    const view = useMemo(() => deriveDeviceCheck(state), [state])
    const stateRef = useRef(state)
    stateRef.current = state

    // ── Les sondes ──
    const probe = useDeviceCheck() as DeviceCheckProbe
    const { causes, isChecking, run } = probe
    const checkingRef = useRef(isChecking)
    checkingRef.current = isChecking
    useEffect(() => dispatch({ type: "causesProbed", causes }), [causes, dispatch])
    useEffect(() => dispatch({ type: "checkingChanged", checking: isChecking }), [isChecking, dispatch])

    const network = useNetworkCheck() as {
        latency: number | null
        isMeasuring: boolean
        measure: () => Promise<number>
    }
    useEffect(() => {
        if (network.latency != null) dispatch({ type: "networkMeasured", latencyMs: network.latency })
    }, [network.latency, dispatch])

    const media = useMediaDevices() as {
        devices: { audioinput: MediaDeviceInfo[]; videoinput: MediaDeviceInfo[] }
        refresh: () => Promise<void>
    }
    const { devices: mediaDevices, refresh: refreshDevices } = media
    useEffect(() => {
        const toOption = (device: MediaDeviceInfo) => ({ deviceId: device.deviceId, label: device.label })
        dispatch({
            type: "devicesListed",
            audioinput: mediaDevices.audioinput.map(toOption),
            videoinput: mediaDevices.videoinput.map(toOption),
        })
    }, [mediaDevices, dispatch])
    // Le navigateur ne nomme les périphériques qu'une fois l'accès accordé.
    useEffect(() => {
        void refreshDevices()
    }, [causes.microphone, causes.camera, refreshDevices])

    const memory = useRememberedCheck(verified) as {
        ready: boolean
        matches: boolean
        reason: string | null
        changedParts: string[]
        read: (chosen?: {
            audioinput?: string | null
            videoinput?: string | null
        }) => Promise<VerifiedDeviceCheck | null>
    }
    useEffect(() => {
        if (memory.ready) {
            dispatch({
                type: "memoryRead",
                matches: memory.matches,
                reason: memory.reason,
                changedParts: memory.changedParts ?? [],
            })
        }
    }, [memory.ready, memory.matches, memory.reason, memory.changedParts, dispatch])

    useEffect(() => dispatch({ type: "videoMutedChanged", muted: isVideoMuted }), [isVideoMuted, dispatch])

    const previewRef = useRef<unknown>(null)
    const image = (
        useCameraObstruction as unknown as (input: { enabled: boolean; previewRef: RefObject<unknown> }) => {
            isObstructed: boolean
            image: ImageReading
        }
    )({ enabled: view.analysingImage, previewRef })
    useEffect(
        () => dispatch({ type: "imageAnalysed", obstructed: image.isObstructed, image: image.image }),
        [image.isObstructed, image.image, dispatch]
    )

    const listening = microphoneListening(view, options.releaseMicrophone === true)
    const micLevel = useMicrophoneLevel({ microphone: listening }, state.audioDeviceId) as number
    useEffect(() => {
        if (listening && micLevel > SIGNAL_THRESHOLD) dispatch({ type: "micHeard" })
    }, [listening, micLevel, dispatch])

    // ── Les délais ──
    useEffect(() => {
        if (!view.micGraceRunning) return undefined
        dispatch({ type: "micGraceStarted" })
        const timer = setTimeout(() => dispatch({ type: "micGraceElapsed" }), MIC_GRACE_MS)
        return () => clearTimeout(timer)
    }, [view.micGraceRunning, causes.microphone, dispatch])

    useEffect(() => {
        const held = state.heldId
        if (held == null) return undefined
        const timer = setTimeout(() => dispatch({ type: "holdElapsed", step: held }), RESOLVED_HOLD_MS[held])
        return () => clearTimeout(timer)
    }, [state.heldId, dispatch])

    // ── Les boucles ──
    const recheck = view.deviceToRecheck
    useEffect(() => {
        if (recheck == null) return undefined
        const probeAgain = () => {
            if (checkingRef.current) return
            void run({
                request: false,
                audioDeviceId: stateRef.current.audioDeviceId,
                videoDeviceId: stateRef.current.videoDeviceId,
                only: recheck,
            })
        }
        const timer = setInterval(probeAgain, 5000)
        // Le retour dans l'onglet (après la fenêtre d'autorisation, un détour par les réglages) ne
        // rouvre que l'appareil en défaut.
        const onFocus = () => probeAgain()
        if (IS_WEB && typeof window !== "undefined") window.addEventListener("focus", onFocus)
        return () => {
            clearInterval(timer)
            if (IS_WEB && typeof window !== "undefined") window.removeEventListener("focus", onFocus)
        }
    }, [recheck, run])

    useInterval(
        () => {
            if (!network.isMeasuring) void network.measure()
        },
        view.networkPolling ? 9000 : null
    )

    // ── Ce qui part une fois ──
    useEffect(() => {
        optionsRef.current.onDevicesChange?.({ audioinput: state.audioDeviceId, videoinput: state.videoDeviceId })
    }, [state.audioDeviceId, state.videoDeviceId])

    // On ne se souvient que d'un parcours réussi, et on relit l'empreinte à cet instant.
    const rememberedRef = useRef(false)
    useEffect(() => {
        if (!view.shouldRemember || rememberedRef.current) return
        rememberedRef.current = true
        let cancelled = false
        memory
            .read({ audioinput: stateRef.current.audioDeviceId, videoinput: stateRef.current.videoDeviceId })
            .then((read) => {
                if (!cancelled && read) optionsRef.current.onVerified(read)
            })
            .catch((error: unknown) => telemetry.error("remember", error))
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view.shouldRemember])

    // Le souvenir vaut mais la connexion laisse une étape : ouvrir les accès une fois, sans fenêtre.
    const openedForMemoryRef = useRef(false)
    useEffect(() => {
        if (!view.memoryLeavesWork || openedForMemoryRef.current) return
        openedForMemoryRef.current = true
        void run({ request: true })
    }, [view.memoryLeavesWork, run])

    // ── La mélodie ──
    const chimeRunRef = useRef(0)
    const mountedRef = useRef(true)
    useEffect(
        () => () => {
            mountedRef.current = false
        },
        []
    )
    const playChime = useCallback(() => {
        chimeRunRef.current += 1
        const current = chimeRunRef.current
        dispatch({ type: "chimeStarted", run: current })
        void (async () => {
            let player: unknown = null
            try {
                player = await playSoundOnce(chimeSource)
            } catch (error) {
                // Le lecteur n'a pas pu naître : la lecture n'a pas eu lieu, l'écran le dira.
                telemetry.error("chime", error)
                if (mountedRef.current) dispatch({ type: "chimeFinished", run: current, played: false })
                return
            }
            const played = (await playbackStarted(player, CHIME_MS)) as boolean
            if (mountedRef.current) dispatch({ type: "chimeFinished", run: current, played })
        })()
    }, [chimeSource, dispatch, telemetry])

    return {
        view,
        previewRef,
        micLevel,
        audioDeviceId: state.audioDeviceId,
        videoDeviceId: state.videoDeviceId,
        isVideoMuted,
        start: useCallback(() => {
            dispatch({ type: "started" })
            void run({ request: true })
        }, [dispatch, run]),
        recheck: useCallback(() => dispatch({ type: "recheckRequested" }), [dispatch]),
        skip: useCallback((step: DeviceCheckStepId) => dispatch({ type: "stepSkipped", step }), [dispatch]),
        reopen: useCallback((step: DeviceCheckStepId) => dispatch({ type: "stepReopened", step }), [dispatch]),
        answerSpeaker: useCallback((heard: boolean) => dispatch({ type: "speakerAnswered", heard }), [dispatch]),
        answerSeesSelf: useCallback((sees: boolean) => dispatch({ type: "seesSelfAnswered", sees }), [dispatch]),
        nextAttempt: useCallback(() => dispatch({ type: "nextAttempt" }), [dispatch]),
        micNoSignal: useCallback(() => dispatch({ type: "micNoSignal" }), [dispatch]),
        // `disabled` pendant une sonde : deux demandes concurrentes donnent le « périphérique occupé »
        // que ce test passe justement son temps à diagnostiquer.
        authorize: useCallback(() => {
            if (!checkingRef.current) void run({ request: true })
        }, [run]),
        openSettings: useCallback(() => {
            Linking.openSettings().catch((error: unknown) => telemetry.error("open-settings", error))
        }, [telemetry]),
        selectDevice: useCallback(
            (kind: MediaDeviceKind, deviceId: string) => {
                dispatch({ type: "deviceSelected", kind, deviceId })
                // Micro seulement : pour la caméra, l'aperçu ouvre l'objectif choisi et rapporte son
                // échec ; sonder les deux ouvrirait deux captures sur une webcam qui n'en veut qu'une.
                if (kind === "audioinput") {
                    void run({
                        request: true,
                        only: "microphone",
                        audioDeviceId: deviceId,
                        videoDeviceId: stateRef.current.videoDeviceId,
                    })
                }
            },
            [dispatch, run]
        ),
        playChime,
        setVideoMuted: useCallback((muted: boolean) => optionsRef.current.onSetVideoMuted(muted), []),
        onPreviewError: useCallback(
            (cause: DeviceCause | null) => dispatch({ type: "previewDeviceError", cause }),
            [dispatch]
        ),
    }
}
